import { useCallback, useEffect, useState } from 'react'
import {
  getAccountCredits,
  grantCredits,
  listAiUsageLog,
  type RemoteAccountCredits,
  type RemoteAiUsageEntry
} from '../lib/adminClient'
import { CREDIT_BUCKETS, creditsToUsd, tierName, tierTone, usdToCredits } from '../lib/tiers'
import { credits as fmtCredits, dateTime, day, marginPct, num, usd, usdFine } from '../lib/format'
import { isMissingEndpoint } from './LoadError'
import { DetailGrid, ExpandableRow, useOpenRow } from './RowDetail'
import { AccountLink } from './Drill'

// One account's AI credits: balance by bucket (allowance, bonus, purchased),
// expiry and reset date, a grant form in CREDITS, and the account's metered-call
// log. Growth spec section 2. Used from the Users page and the AI page.
//
// Every figure is the server's. When the server does not have an endpoint yet,
// the section says so by name instead of showing zeros.

type Load<T> =
  | { state: 'loading' }
  | { state: 'ready'; data: T }
  | { state: 'missing' }
  | { state: 'error'; error: string }

function bucketLabel(kind: string): string {
  return CREDIT_BUCKETS.find((b) => b.kind === kind)?.label ?? kind
}

function bucketRule(kind: string): string | undefined {
  return CREDIT_BUCKETS.find((b) => b.kind === kind)?.rule
}

function allowanceText(a: RemoteAccountCredits['allowance']): string {
  if (a === null || a === undefined) return '—'
  if (typeof a === 'number') return `${fmtCredits(a)} / month`
  const parts = [`${fmtCredits(a.monthly)} / month`]
  if (typeof a.used === 'number') parts.push(`${num(a.used)} used`)
  if (typeof a.remaining === 'number') parts.push(`${num(a.remaining)} left`)
  return parts.join(' · ')
}

export function AccountCreditsPanel({
  accountId,
  canGrant = true,
  onGranted
}: {
  accountId: string
  canGrant?: boolean
  onGranted?: () => void
}): JSX.Element {
  const [credits, setCredits] = useState<Load<RemoteAccountCredits>>({ state: 'loading' })
  const bucketOpen = useOpenRow()
  const [log, setLog] = useState<Load<RemoteAiUsageEntry[]>>({ state: 'loading' })
  const [amount, setAmount] = useState('500')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  const loadCredits = useCallback(async () => {
    const res = await getAccountCredits(accountId)
    if (res.ok) setCredits({ state: 'ready', data: res.data })
    else if (isMissingEndpoint(res.status, res.error)) setCredits({ state: 'missing' })
    else setCredits({ state: 'error', error: res.error })
  }, [accountId])

  const loadLog = useCallback(async () => {
    const res = await listAiUsageLog({ accountId, limit: 25 })
    if (res.ok) setLog({ state: 'ready', data: res.data.entries ?? [] })
    else if (isMissingEndpoint(res.status, res.error)) setLog({ state: 'missing' })
    else setLog({ state: 'error', error: res.error })
  }, [accountId])

  useEffect(() => {
    setCredits({ state: 'loading' })
    setLog({ state: 'loading' })
    void loadCredits()
    void loadLog()
  }, [loadCredits, loadLog])

  const parsed = Number(amount)
  const valid = Number.isInteger(parsed) && parsed > 0

  async function grant(): Promise<void> {
    if (!valid || busy) return
    setBusy(true)
    setNotice(null)
    const res = await grantCredits(accountId, parsed, reason.trim())
    setBusy(false)
    if (!res.ok) {
      setNotice({ tone: 'error', text: `Grant failed: ${res.error}` })
      return
    }
    // The new balance as the server reports it, in credits. A legacy server
    // answers in billed dollars, converted at the definition of a credit.
    const balance =
      typeof res.data.balance === 'number'
        ? res.data.balance
        : typeof res.data.balanceUsd === 'number'
          ? usdToCredits(res.data.balanceUsd)
          : null
    setNotice({
      tone: 'ok',
      text: `Granted ${fmtCredits(parsed)}.${balance !== null ? ` Balance now ${fmtCredits(balance)}.` : ''}`
    })
    setReason('')
    void loadCredits()
    onGranted?.()
  }

  return (
    <div className="space-y-4" data-testid="account-credits">
      <section>
        <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">AI credits</div>
        {credits.state === 'loading' && <div className="text-xs text-slate-500">Loading credits…</div>}
        {credits.state === 'missing' && (
          <div className="text-xs text-amber-300" data-testid="credits-endpoint-missing">
            This server does not provide <code>GET /admin/accounts/:id/credits</code> yet, so bucket balances, expiry and
            the reset date cannot be shown. The AI page lists the account's lifetime ledger.
          </div>
        )}
        {credits.state === 'error' && (
          <div className="text-xs text-red-300" role="alert">Could not load credits: {credits.error}</div>
        )}
        {credits.state === 'ready' && (
          <div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <div className="text-2xl font-semibold tabular-nums" data-testid="credits-balance">{fmtCredits(credits.data.balance)}</div>
              <div className="text-xs text-slate-500">= {usd(creditsToUsd(credits.data.balance))} of billed usage</div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tierTone(credits.data.tier)}`}>
                {tierName(credits.data.tier)}
              </span>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Allowance {allowanceText(credits.data.allowance)} · resets {day(credits.data.resetsAt)}
            </div>
            {credits.data.buckets.length === 0 ? (
              <div className="text-xs text-slate-500 mt-2">No credit buckets on this account.</div>
            ) : (
              <table className="admin-table mt-2" data-testid="credit-buckets">
                <thead>
                  <tr>
                    <th>Bucket</th>
                    <th className="text-right">Credits</th>
                    <th>Source</th>
                    <th>Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {credits.data.buckets.map((b, i) => (
                    <ExpandableRow
                      key={`${b.kind}-${i}`}
                      open={bucketOpen.isOpen(String(i))}
                      onToggle={() => bucketOpen.toggle(String(i))}
                      colSpan={4}
                      testId={`credit-bucket-${i}`}
                      detail={() => (
                        <DetailGrid
                          facts={[
                            ['Bucket', bucketLabel(b.kind)],
                            ['Credits', num(b.credits)],
                            ['Worth', usd(creditsToUsd(b.credits))],
                            ['Source', b.source],
                            ['Granted', b.grantedAt ? dateTime(b.grantedAt) : null],
                            ['Expires', b.kind === 'allowance' ? `Resets ${dateTime(credits.data.resetsAt)}` : b.expiresAt ? dateTime(b.expiresAt) : 'Never']
                          ]}
                          wide={bucketRule(b.kind) ? [['Rule', bucketRule(b.kind)]] : undefined}
                        />
                      )}
                    >
                      <td title={bucketRule(b.kind)}>{bucketLabel(b.kind)}</td>
                      <td className="text-right tabular-nums">{num(b.credits)}</td>
                      <td className="text-slate-400">{b.source ?? '—'}</td>
                      <td className="text-slate-400">{b.kind === 'allowance' ? `resets ${day(credits.data.resetsAt)}` : day(b.expiresAt)}</td>
                    </ExpandableRow>
                  ))}
                </tbody>
              </table>
            )}
            <div className="text-[11px] text-slate-500 mt-2">Spent in order: allowance, then bonus, then purchased.</div>
          </div>
        )}
      </section>

      {canGrant && (
        <section className="pt-3 border-t border-white/5">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Grant credits</div>
          <div className="flex gap-2 items-start flex-wrap">
            <label className="block">
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
                inputMode="numeric"
                aria-label="Credits to grant"
                data-testid="grant-credits-amount"
                className="w-28 bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:border-accent"
              />
              <div className="text-[11px] text-slate-500 mt-1">{valid ? `= ${usd(creditsToUsd(parsed))} billed value` : 'Whole credits, more than 0'}</div>
            </label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (goes to the audit log)"
              aria-label="Reason"
              className="flex-1 min-w-[180px] bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-accent"
            />
            <button className="btn-primary" disabled={!valid || busy} onClick={() => void grant()} data-testid="grant-credits-submit">
              {busy ? 'Granting…' : 'Grant'}
            </button>
          </div>
          {notice && (
            <div
              role={notice.tone === 'error' ? 'alert' : 'status'}
              data-testid="grant-credits-notice"
              className={`text-xs mt-2 ${notice.tone === 'error' ? 'text-red-300' : 'text-emerald-300'}`}
            >
              {notice.text}
            </div>
          )}
        </section>
      )}

      <section className="pt-3 border-t border-white/5">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Recent metered calls</div>
        {log.state === 'loading' && <div className="text-xs text-slate-500">Loading usage…</div>}
        {log.state === 'missing' && (
          <div className="text-xs text-amber-300">
            This server does not provide <code>GET /admin/ai-usage/log</code> yet, so per-call usage cannot be shown.
          </div>
        )}
        {log.state === 'error' && <div className="text-xs text-red-300" role="alert">Could not load usage: {log.error}</div>}
        {log.state === 'ready' && log.data.length === 0 && <div className="text-xs text-slate-500">No metered calls on this account.</div>}
        {log.state === 'ready' && log.data.length > 0 && <UsageLogTable entries={log.data} showAccount={false} />}
      </section>
    </div>
  )
}

export function UsageLogTable({ entries, showAccount }: { entries: RemoteAiUsageEntry[]; showAccount: boolean }): JSX.Element {
  const rowOpen = useOpenRow()
  return (
    <div className="overflow-x-auto">
      <table className="admin-table" data-testid="usage-log">
        <thead>
          <tr>
            <th>When</th>
            {showAccount && <th>Account</th>}
            <th>Model</th>
            <th className="text-right">Tokens in / out</th>
            <th className="text-right">Anthropic cost</th>
            <th className="text-right">Billed</th>
            <th className="text-right">Credits</th>
            <th className="text-right">Margin</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <ExpandableRow
              key={e.id}
              open={rowOpen.isOpen(e.id)}
              onToggle={() => rowOpen.toggle(e.id)}
              colSpan={showAccount ? 8 : 7}
              testId={`usage-row-${e.id}`}
              detail={() => (
                <DetailGrid
                  facts={[
                    ['When', dateTime(e.at)],
                    ['Account', e.email ? <AccountLink id={e.accountId}>{e.email}</AccountLink> : null],
                    ['Account id', <AccountLink id={e.accountId}><span className="font-mono">{e.accountId}</span></AccountLink>],
                    ['Model', <span className="font-mono">{e.model}</span>],
                    ['Input tokens', num(e.inputTokens)],
                    ['Output tokens', num(e.outputTokens)],
                    ['Cache read tokens', typeof e.cacheReadTokens === 'number' ? num(e.cacheReadTokens) : null],
                    ['Cache write tokens', typeof e.cacheWriteTokens === 'number' ? num(e.cacheWriteTokens) : null],
                    ['Anthropic cost', usdFine(e.rawCostUsd)],
                    ['Billed', usdFine(e.billedUsd)],
                    ['Credits', num(typeof e.credits === 'number' ? e.credits : usdToCredits(e.billedUsd))],
                    ['Margin', marginPct(e.rawCostUsd, e.billedUsd)],
                    ['Entry id', <span className="font-mono">{e.id}</span>]
                  ]}
                />
              )}
            >
              <td className="text-slate-400 whitespace-nowrap">{dateTime(e.at)}</td>
              {showAccount && <td className="text-slate-300"><AccountLink id={e.accountId}>{e.email ?? e.accountId}</AccountLink></td>}
              <td className="font-mono text-[11px]">{e.model}</td>
              <td className="text-right tabular-nums">
                {num(e.inputTokens)} / {num(e.outputTokens)}
                {e.cacheReadTokens ? <div className="text-[10px] text-slate-500">cache read {num(e.cacheReadTokens)}</div> : null}
              </td>
              <td className="text-right tabular-nums">{usdFine(e.rawCostUsd)}</td>
              <td className="text-right tabular-nums">{usdFine(e.billedUsd)}</td>
              <td className="text-right tabular-nums">{num(typeof e.credits === 'number' ? e.credits : usdToCredits(e.billedUsd))}</td>
              <td className="text-right tabular-nums">{marginPct(e.rawCostUsd, e.billedUsd)}</td>
            </ExpandableRow>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function CreditsModal({
  accountId,
  title,
  onClose,
  onGranted
}: {
  accountId: string
  title: string
  onClose: () => void
  onGranted?: () => void
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={onClose}>
      <div
        className="glass-card rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`AI credits for ${title}`}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">AI credits</div>
            <h2 className="text-lg font-semibold mt-1">{title}</h2>
          </div>
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
        <AccountCreditsPanel accountId={accountId} onGranted={onGranted} />
      </div>
    </div>
  )
}
